// src/pages/farms/components/ConvidarResponsavelDialog.js
// Modal de geração do convite nominal (PG-042).
//
// Toda decisão está em ../../ConviteFazenda/conviteFazendaLogica.js — este
// arquivo é só a casca visual. Duas regras que ele NÃO pode quebrar:
//   1. a mensagem do backend é exibida literalmente, sem prefixo e sem mapa;
//   2. o botão de copiar só existe quando há link não-vazio para copiar.

import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { Alert, Box, Typography } from '@mui/material'

import AppButton from '../../../components/material/AppButton'
import TextField from '../../../components/material/TextField'
import { recordError, recordEvent } from '../../../observability/events'
import {
  copiarParaAreaDeTransferencia,
  enviarConvite,
  extrairMensagemDeErro,
  houveErroDeBackend,
  linkParaCopiar,
  MENSAGEM_SEM_RESPOSTA,
  podeCopiarLink,
  prepararConvite,
} from '../../ConviteFazenda/conviteFazendaLogica'

export default function ConvidarResponsavelDialog({
  aberto,
  fazenda,
  onClose,
}) {
  const dispatch = useDispatch()

  const [email, setEmail] = useState('')
  const [prazo, setPrazo] = useState('')
  const [erros, setErros] = useState({})
  const [enviando, setEnviando] = useState(false)
  const [link, setLink] = useState(null)
  const [mensagemDoBackend, setMensagemDoBackend] = useState(null)
  const [semResposta, setSemResposta] = useState(false)
  const [copiado, setCopiado] = useState(false)

  // Guarda de cancelamento (mesmo padrão do `cancelado` de ConviteFazenda.js):
  // este modal não desmonta ao fechar — `aberto` só controla a prop `open` do
  // MUI Dialog — então uma resposta que chega depois de fechado (ou depois de
  // um novo envio ter começado) não pode sobrescrever o estado com um
  // resultado obsoleto que reaparece na próxima vez que o modal abrir.
  const requisicaoAtualRef = useRef(0)

  useEffect(
    () => () => {
      requisicaoAtualRef.current += 1
    },
    [],
  )

  const fechar = () => {
    requisicaoAtualRef.current += 1
    setEmail('')
    setPrazo('')
    setErros({})
    setEnviando(false)
    setLink(null)
    setMensagemDoBackend(null)
    setSemResposta(false)
    setCopiado(false)
    onClose()
  }

  const confirmar = async () => {
    // Higiene de tipo/forma ANTES de qualquer requisição. Um e-mail
    // malformado ou um prazo não-inteiro fazem o backend recusar pelo
    // ModelState, e essa recusa volta em string crua — que o helper de API
    // lê como sucesso. Checar aqui, síncrono, evita o spinner piscar numa
    // entrada que nunca ia gerar requisição nenhuma.
    const preparado = prepararConvite({
      fazendaId: fazenda.id,
      emailConvidado: email,
      prazo,
    })
    setErros(preparado.erros || {})
    if (!preparado.valido) return

    const minhaRequisicao = ++requisicaoAtualRef.current
    const requisicaoAindaAtual = () =>
      requisicaoAtualRef.current === minhaRequisicao

    setMensagemDoBackend(null)
    setSemResposta(false)
    setLink(null)
    setCopiado(false)
    setEnviando(true)

    try {
      const resultado = await enviarConvite(dispatch, {
        fazendaId: fazenda.id,
        emailConvidado: email,
        prazo,
      })

      if (!requisicaoAindaAtual()) return

      if (!resultado) {
        // Sem resposta do backend (offline/rede). Não há mensagem dele a
        // exibir, e ficar em silêncio faria a tela parecer que deu certo.
        setSemResposta(true)
      } else if (houveErroDeBackend(resultado)) {
        // Caminho ÚNICO de exibição: o que veio, como veio.
        setMensagemDoBackend(extrairMensagemDeErro(resultado))
      } else {
        setLink(linkParaCopiar(resultado))
        recordEvent({
          level: 'info',
          category: 'farm',
          action: 'farm_invite_generated',
          // Explícito por hábito do fluxo de convite (RV-1): esta tela vive
          // em /farms/:id/update, que não carrega token — mas a rota do
          // aceite (que carrega) importa deste mesmo módulo, e manter as
          // duas casca sempre passando `url` explícito evita que um refactor
          // futuro mova este recordEvent para um lugar sensível sem notar.
          payload: { farmId: fazenda?.id, url: window.location.pathname },
        })
      }
    } catch (e) {
      if (!requisicaoAindaAtual()) return
      recordError(e instanceof Error ? e : new Error(String(e)), {
        category: 'farm',
        action: 'farm_invite_generate_failed',
        payload: { farmId: fazenda?.id, url: window.location.pathname },
      })
      setMensagemDoBackend(null)
      setSemResposta(true)
    } finally {
      if (requisicaoAindaAtual()) setEnviando(false)
    }
  }

  const copiar = async () => {
    const ok = await copiarParaAreaDeTransferencia(link)
    setCopiado(ok)
  }

  return (
    <Dialog
      maxWidth='sm'
      fullWidth
      open={Boolean(aberto)}
      aria-labelledby='convidar-responsavel-titulo'
    >
      <DialogTitle
        id='convidar-responsavel-titulo'
        sx={{ color: 'text.secondary' }}
      >
        Convidar responsável
      </DialogTitle>

      <DialogContent>
        <Typography variant='body2' sx={{ mb: 2 }}>
          Gere um link para quem já tem conta no ProGERAR. Quem receber o link
          vira responsável por esta fazenda ao abri-lo.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            id='emailConvidado'
            label='E-mail do convidado'
            type='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(erros.emailConvidado)}
            helperText={erros.emailConvidado}
            disabled={enviando}
          />
          <TextField
            id='expiraEmHoras'
            label='Prazo em horas (opcional)'
            value={prazo}
            onChange={(e) => setPrazo(e.target.value)}
            error={Boolean(erros.prazo)}
            helperText={erros.prazo}
            disabled={enviando}
          />
        </Box>

        {mensagemDoBackend && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {mensagemDoBackend}
          </Alert>
        )}

        {semResposta && (
          <Alert severity='warning' sx={{ mt: 2 }}>
            {MENSAGEM_SEM_RESPOSTA}
          </Alert>
        )}

        {podeCopiarLink({ link }) && (
          <Box sx={{ mt: 2 }}>
            <Alert severity='success' sx={{ mb: 1 }}>
              Convite gerado. Envie o link para o convidado.
            </Alert>
            <Typography
              variant='body2'
              sx={{ wordBreak: 'break-all', mb: 1 }}
              data-testid='link-do-convite'
            >
              {link}
            </Typography>
            <AppButton variant='secondary' onClick={copiar}>
              {copiado ? 'Link copiado' : 'Copiar link'}
            </AppButton>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <AppButton variant='tertiary' onClick={fechar}>
          Fechar
        </AppButton>
        <AppButton variant='primary' onClick={confirmar} disabled={enviando}>
          Gerar link
        </AppButton>
      </DialogActions>
    </Dialog>
  )
}
